import { Check, ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    checklistStepTask,
    GET_STARTED_STEPS,
} from '@apps/web/features/learn_phoebe/model/checklist_steps';
import { useGetStartedChecklist } from '@apps/web/features/learn_phoebe/model/use_get_started_checklist';
import { CopyPromptAction } from '@apps/web/features/learn_phoebe/ui/copy_prompt_action';
import { FakeChat } from '@apps/web/features/learn_phoebe/ui/fake_chat';
import { cn } from '@apps/web/lib/utils';

// The sidebar "Learn Phoebe" checklist: a short list of agent tasks a
// scheduler tries once. "Try it" opens the chat with the prompt already in the composer and
// marks the step complete; usage-derived completion (the GET) remains the
// backstop for members who do a task on their own.
export function GetStartedChecklist() {
    const {
        isVisible,
        completedStepIds,
        completedCount,
        isOpen,
        selectedStep,
        demoKey,
        setIsOpen,
        selectStep,
        tryStep,
        openLearnPhoebePage,
    } = useGetStartedChecklist();
    const selectedTask = checklistStepTask(selectedStep);

    if (!isVisible) {
        return null;
    }

    return (
        // The Intercom launcher floats over the bottom-left corner (40px of
        // vertical padding, see intercom_provider), which is exactly where the
        // sidebar footer sits — clear it so the card stays clickable.
        <div className="mb-24 group-data-[state=collapsed]:hidden">
            <button
                type="button"
                onClick={() => {
                    setIsOpen(true);
                }}
                className="bg-card hover:bg-muted/60 flex w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-left shadow-sm"
            >
                <ProgressRing completed={completedCount} />
                <span className="flex-1 text-sm font-semibold">
                    Learn Phoebe
                </span>
                <span className="text-muted-foreground text-xs">
                    {completedCount} / {GET_STARTED_STEPS.length}
                </span>
                <ChevronDown className="text-muted-foreground size-4 shrink-0 rotate-180" />
            </button>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Learn Phoebe</DialogTitle>
                        {/* The hand-off sits in the header, not under the
                            chat: the played conversation grows the dialog, so
                            anything below it moves while you read. */}
                        <div className="flex items-baseline justify-between gap-3 pr-6">
                            <DialogDescription>
                                Five things to try in chat.
                            </DialogDescription>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={openLearnPhoebePage}
                                className="h-7 shrink-0 px-2.5 text-xs"
                            >
                                See all examples
                            </Button>
                        </div>
                    </DialogHeader>
                    <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                        <div className="flex flex-col gap-0.5">
                            {GET_STARTED_STEPS.map((step) => {
                                const isDone = completedStepIds.has(step.id);
                                const isSelected = selectedStep.id === step.id;
                                // The open step is the subject: full-strength
                                // text on a tinted block. Everything else
                                // recedes, so the eye lands here first.
                                return (
                                    <div
                                        key={step.id}
                                        className={cn(
                                            'rounded-md',
                                            isSelected && 'bg-muted/70',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                selectStep(step.id);
                                            }}
                                            aria-expanded={isSelected}
                                            aria-controls={`checklist-step-panel-${step.id}`}
                                            className={cn(
                                                'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm',
                                                isSelected
                                                    ? 'text-foreground font-medium'
                                                    : cn(
                                                          'hover:bg-muted/60',
                                                          isDone
                                                              ? 'text-muted-foreground'
                                                              : 'text-foreground',
                                                      ),
                                            )}
                                        >
                                            <StepCheck isDone={isDone} />
                                            <span className="min-w-0 flex-1">
                                                {checklistStepTask(step).title}
                                            </span>
                                            <ChevronRight
                                                aria-hidden="true"
                                                className={cn(
                                                    'text-muted-foreground size-4 shrink-0 transition-transform',
                                                    isSelected && 'rotate-90',
                                                )}
                                            />
                                        </button>
                                        <div
                                            id={`checklist-step-panel-${step.id}`}
                                        >
                                            {isSelected ? (
                                                <div className="px-2 pb-2.5 pl-8">
                                                    <p className="text-muted-foreground text-sm leading-6">
                                                        {
                                                            checklistStepTask(
                                                                step,
                                                            ).tip
                                                        }
                                                    </p>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex min-w-0 flex-col">
                            {/* Keyed so re-picking the open step replays it. */}
                            <FakeChat
                                key={demoKey}
                                task={selectedTask}
                                className="min-h-0 flex-1"
                            />
                            {/* Both ways to take the prompt live together: run
                                it here, or copy it and type it yourself. They
                                wrap rather than overflow on a narrow dialog. */}
                            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        tryStep(selectedStep);
                                    }}
                                    className="h-7 px-2.5 text-xs"
                                >
                                    Try in chat
                                </Button>
                                <CopyPromptAction
                                    prompt={selectedTask.prompt}
                                    surface="checklist_dialog"
                                    taskId={selectedTask.id}
                                    stepId={selectedStep.id}
                                    className="text-xs [&_svg]:size-3.5"
                                />
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ProgressRing({ completed }: { readonly completed: number }) {
    const radius = 6;
    const circumference = 2 * Math.PI * radius;
    const fraction = completed / GET_STARTED_STEPS.length;
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className="size-4 shrink-0 -rotate-90"
        >
            <circle
                cx="8"
                cy="8"
                r={radius}
                fill="none"
                strokeWidth="2.5"
                className="stroke-muted"
            />
            <circle
                cx="8"
                cy="8"
                r={radius}
                fill="none"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - fraction)}
                className="stroke-primary transition-[stroke-dashoffset] duration-300"
            />
        </svg>
    );
}

function StepCheck({ isDone }: { readonly isDone: boolean }) {
    return (
        <span
            className={cn(
                'flex size-4 shrink-0 items-center justify-center rounded-full border',
                isDone && 'border-primary bg-primary text-primary-foreground',
            )}
        >
            {isDone ? <Check className="size-3" /> : null}
        </span>
    );
}
